import { useContext, useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ForgotPasswordProvider from "./components/context/ForgotPasswordContext";
import { GlobalStateContext } from "./components/context/GlobalStateContext";
import { logout, renewTokenUser } from "./redux/features/auth/authSlice";
import { getCompanyConfig } from "./services/authApi";
import { jwtDecode } from "jwt-decode";
import { useDoctorWebPresenceHeartbeat } from "./hooks/useDoctorWebPresenceHeartbeat";
import { readSafeStorage, writeSafeStorage } from "./utils/localStorageHelper";

import Login from "./screen/auth/Login";
import SignUp from "./screen/auth/SignUp";
import ForgotPassword from "./screen/auth/ForgotPassword";
import NewPassword from "./screen/auth/NewPassword";
import PurchasePage from "./pages/PurchasePage";
import PurchaseProductPage from "./pages/PurchaseProductPage";
import ProductListPage from "./pages/ProductListPage";
import TransactionListPage from "./pages/TransactionListPage";
import Chatbox from "./components/common/Chatbox";
import DoctorChatPage from "./pages/DoctorChatPage";
import DoctorDashboardPage from "./pages/DoctorDashboardPage";
import HistoryDoctorPage from "./pages/HistoryDoctorPage";
import ChangeAddressPage from "./pages/ChangeAddressPage";
import OtpConfirmation from "./screen/auth/OtpConfirmation";
import CheckoutPurchaseNew from "./screen/purchase/checkout/CheckoutPurchaseNew";
import BlankPage from "./pages/BlankPage";
import ProductCategoryListPage from "./pages/ProductCategoryListPage";
import WaitingForPayment from "./screen/purchase/checkout/WaitingForPayment";
import DetailTransactionPage from "./pages/DetailTransactionPage";
import CheckoutPaymentSuccess from "./screen/purchase/checkout/CheckoutPaymentSuccess";
import ConsultationDetailFormGuard from "./components/common/ConsultationDetailFormGuard";
import WaitingRoomConsultation from "./screen/telemedicine/WaitingRoomConsultation";
import ConsultationRating from "./screen/telemedicine/ConsultationRating";
import TinodeChatCustomer from "./screen/telemedicine/tinodeChat/TinodeChatCustomer";
import DoctorProfilePage from "./pages/DoctorProfilePage";
import CartDetailsPage from "./pages/CartDetailsPage";
import ProfilePage from "./pages/ProfilePage";
import SetLocationPage from "./pages/SetLocationPage";
import ListMyAddressPage from "./pages/ListMyAddressPage";
import AddNewAddressPage from "./pages/AddNewAddressPage";
import CategoryListPage from "./pages/CategoryListPage";
import CheckoutPurchasePage from "./pages/CheckoutPurchasePage";
import ChangeProfilePage from "./pages/ChangeProfilePage";
import ConsultationHistoryPage from "./pages/ConsultationHistoryPage";
import PurchasePaymentMethodPage from "./pages/PurchasePaymentMethodPage";

function App() {
  const { setIsLogin, setIsMobileView } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  const [isDoctorActive, setIsDoctorActive] = useState(
    () => readSafeStorage("is_doctor_active", "false") === "true",
  );
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "is_doctor_active") {
        setIsDoctorActive(e.newValue === "true");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const accessToken = useSelector((state) => state.auth.accessToken);
  const isDoctorLoggedIn =
    localStorage.getItem("role") === "2" &&
    !!(Cookies.get("accessToken") || accessToken);

  useDoctorWebPresenceHeartbeat({
    enabled: isDoctorLoggedIn,
    isDoctorActive,
  });
  const refreshTimerRef = useRef(null);
  const expiryCheckIntervalRef = useRef(null);

  const shouldRefreshToken = () => {
    const token = Cookies.get("accessToken") || accessToken;
    if (!token) return false;

    try {
      const decoded = jwtDecode(token);
      const expiryTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      return timeUntilExpiry < 5 * 60 * 1000;
    } catch (error) {
      return true;
    }
  };

  const handleTokenRefresh = async () => {
    if (!Cookies.get("renewToken")) {
      dispatch(logout());
      return;
    }

    dispatch(renewTokenUser());
  };

  useEffect(() => {
    if (shouldRefreshToken()) {
      handleTokenRefresh();
    }

    expiryCheckIntervalRef.current = setInterval(() => {
      if (shouldRefreshToken() && Cookies.get("renewToken")) {
        handleTokenRefresh();
      }
    }, 60 * 1000);

    refreshTimerRef.current = setInterval(
      () => {
        if (Cookies.get("renewToken")) {
          handleTokenRefresh();
        }
      },
      15 * 60 * 1000,
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (shouldRefreshToken()) {
          handleTokenRefresh();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleOnline = () => {
      if (shouldRefreshToken()) {
        handleTokenRefresh();
      }
    };
    window.addEventListener("online", handleOnline);

    return () => {
      clearInterval(expiryCheckIntervalRef.current);
      clearInterval(refreshTimerRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [dispatch]);

  useEffect(() => {
    queryClient
      .prefetchQuery({
        queryKey: ["outlet-config"],
        queryFn: getCompanyConfig,
        staleTime: 5 * 60 * 1000,
      })
      .then(() => {
        const data = queryClient.getQueryData(["outlet-config"]);
        if (!data?.id) return;
        writeSafeStorage("companyId", data.id);
        if (data.logo) writeSafeStorage("outlet_logo", data.logo);
        if (data.name) writeSafeStorage("outlet_name", data.name);
        if (data.address) writeSafeStorage("outlet_address", data.address);
        if (data.city) writeSafeStorage("outlet_city", data.city);
        if (Array.isArray(data.banners) && data.banners.length > 0) {
          writeSafeStorage("outlet_banners", JSON.stringify(data.banners));
        } else {
          writeSafeStorage("outlet_banners", JSON.stringify([]));
        }
      });
  }, [queryClient]);

  useEffect(() => {
    if (Cookies.get("accessToken") || null || "") {
      setIsLogin(true);
    } else {
      setIsLogin(false);
      if (
        pathname !== "/login" &&
        pathname !== "/register" &&
        pathname !== "/forgot-password" &&
        pathname !== "/new-password"
      ) {
        writeSafeStorage("LastPath", pathname);
      }
    }
    if (!readSafeStorage("MachineId", "")) {
      const uuid =
        typeof crypto?.randomUUID === "function"
          ? crypto.randomUUID()
          : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
            });
      writeSafeStorage("MachineId", uuid);
    }

    if (pathname === "/purchase" && !readSafeStorage("companyId", ""))
      navigate("/");
  }, [navigate, setIsLogin]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const hasNoCache = currentUrl.searchParams.has("nocache");

    if (!hasNoCache) {
      currentUrl.searchParams.set("nocache", Date.now().toString());
      window.history.replaceState({}, "", currentUrl.toString());
    }
  }, []);

  return (
    <div className="App">
      <div
        hidden={
          window.location.pathname === "/login" ||
          window.location.pathname === "/register" ||
          window.location.pathname === "/forgot-password" ||
          window.location.pathname === "/new-password" ||
          localStorage.getItem("role") === "2"
        }
      >
        <Chatbox />
      </div>
      <Routes>
        <Route path="/" element={<PurchasePage />} />
        <Route path="/blank" element={<BlankPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<SignUp />} />
        <Route path="/otp-confirmation" element={<OtpConfirmation />} />
        <Route
          path="/forgot-password"
          element={
            <ForgotPasswordProvider>
              <ForgotPassword />
            </ForgotPasswordProvider>
          }
        />
        <Route path="/reset-password" element={<NewPassword />} />
        <Route path="/purchase" element={<Navigate to="/" />} />
        <Route path="/purchase/cart-details" element={<CartDetailsPage />} />
        <Route path="/purchase/category-list" element={<CategoryListPage />} />
        <Route path="/location-address" element={<SetLocationPage />} />
        <Route
          path="/location-address/my-address"
          element={<ListMyAddressPage />}
        />
        <Route
          path="/location-address/my-address/new"
          element={<AddNewAddressPage />}
        />
        <Route
          path="/location-address/my-address/edit/:id"
          element={<AddNewAddressPage />}
        />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/change" element={<ChangeProfilePage />} />
        <Route
          path="/consultation/detail-form"
          element={<ConsultationDetailFormGuard />}
        />
        <Route
          path="/consultation/waiting"
          element={<WaitingRoomConsultation />}
        />
        <Route path="/consultation/rating" element={<ConsultationRating />} />
        <Route
          path="/consultation/history"
          element={<ConsultationHistoryPage />}
        />
        <Route
          path="/consultation/chat-with-doctor"
          element={<TinodeChatCustomer />}
        />
        <Route
          path="/consultation/chat-with-doctor/:topic"
          element={<TinodeChatCustomer />}
        />
        <Route path="/purchase/checkout" element={<CheckoutPurchasePage />} />
        <Route
          path="/purchase/checkout/payment-method"
          element={<PurchasePaymentMethodPage />}
        />
        <Route
          path="/purchase/checkout/waiting-for-payment"
          element={<WaitingForPayment />}
        />
        <Route
          path="/purchase/checkout/success"
          element={<CheckoutPaymentSuccess />}
        />
        <Route
          path="/purchase/payment-method"
          element={<CheckoutPurchaseNew />}
        />
        <Route path="/purchase/outlet/:id" element={<PurchasePage />} />
        <Route
          path="/purchase/product-detail/:productId"
          element={<PurchaseProductPage />}
        />
        <Route
          path="/purchase/category/:id"
          element={<ProductCategoryListPage />}
        />
        <Route
          path="/purchase/search-product/:search"
          element={<ProductListPage />}
        />
        <Route
          path="/purchase/product-category/:categoryId"
          element={<ProductListPage />}
        />
        <Route
          path="/purchase/transaction-list"
          element={<TransactionListPage />}
        />
        <Route
          path="/purchase/detail-transaction/:orderNumber"
          element={<DetailTransactionPage />}
        />
        <Route path="/doctor-dashboard" element={<DoctorDashboardPage />} />
        <Route path="/doctor-profile" element={<DoctorProfilePage />} />
        <Route path="/consultations/:topicId?" element={<DoctorChatPage />} />
        <Route path="/history" element={<HistoryDoctorPage />} />
        <Route path="/profile/change-address" element={<ChangeAddressPage />} />
        <Route path="*" element={<Navigate to="/blank" replace />} />
      </Routes>
      <ToastContainer
        position="top-left"
        autoClose={5000}
        hideProgressBar={false}
      />
    </div>
  );
}

export default App;
